import React from 'react'
import './PrivacyPolicy.css'

const PrivacyPolicy = () => (
    <div className="PrivacyPolicy">
        <h1 className="title">Integritetspolicy för Ögonremiss</h1>
        <p className="updated">Senast uppdaterad: 2026-06-10</p>

        <p>Denna integritetspolicy beskriver hur vi samlar in, använder och skyddar personuppgifter i tjänsten Ögonremiss - ett system för elektronisk remisshantering mellan optiker och ögonkliniker.</p>

        <h2>1. Personuppgiftsansvarig</h2>
        <p>Personuppgiftsansvarig för behandlingen av dina personuppgifter är:</p>
        <p>
            <strong>Bluebricks AB</strong><br/>
            Org.nr: 5566877428<br/>
            E-post: christian.hilmersson@achilmersson.se
        </p>

        <h2>2. Vilka uppgifter vi behandlar</h2>

        <h3>2.1 Patientuppgifter (vid remisshantering)</h3>
        <p>När en optiker skapar en remiss behandlas följande patientuppgifter:</p>
        <ul>
            <li><strong>Personnummer</strong> - för identifiering i vårdsystemet</li>
            <li><strong>Namn och telefonnummer</strong> - för kontakt</li>
            <li><strong>Kliniska undersökningsresultat</strong> - synskärpa, tryck, korrektion</li>
            <li><strong>Beskrivning av besvär</strong> - symtom och varaktighet</li>
            <li><strong>Tolkbehov</strong> - språk om tolk behövs</li>
        </ul>
        <div className="highlight">
            <strong>Viktigt:</strong> Patientuppgifter lagras inte permanent i Ögonremiss. Systemet lagrar endast ett hashat (icke-läsbart) ID för att kunna följa upp remissstatus. Själva patientdata skickas direkt till vårdsystemet och hämtas därifrån vid behov.
        </div>

        <h3>2.2 Användaruppgifter (personal)</h3>
        <p>För optiker och vårdpersonal som använder systemet behandlas:</p>
        <ul>
            <li>Användarnamn och e-postadress</li>
            <li>För- och efternamn</li>
            <li>Telefonnummer</li>
            <li>Roll och organisationstillhörighet</li>
            <li>Inloggningshistorik</li>
        </ul>

        <h3>2.3 Organisationsuppgifter</h3>
        <p>För anslutna optiker och vårdenheter behandlas:</p>
        <ul>
            <li>Organisationsnamn och HSA-ID</li>
            <li>Kontaktperson, e-post och telefon</li>
            <li>Adressuppgifter</li>
        </ul>

        <h2>3. Varför vi behandlar uppgifterna</h2>
        <p>Vi behandlar personuppgifter för följande ändamål:</p>
        <table>
            <thead>
                <tr>
                    <th>Ändamål</th>
                    <th>Beskrivning</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Remisshantering</td>
                    <td>Skicka och följa upp remisser mellan optiker och vårdenheter</td>
                </tr>
                <tr>
                    <td>Användarhantering</td>
                    <td>Skapa och administrera användarkonton</td>
                </tr>
                <tr>
                    <td>Statusuppföljning</td>
                    <td>Informera optiker om remissens status (mottagen, bokad, avslutad)</td>
                </tr>
                <tr>
                    <td>Säkerhetsloggning</td>
                    <td>Spåra åtkomst och ändringar för säkerhet och felsökning</td>
                </tr>
            </tbody>
        </table>

        <h2>4. Rättslig grund</h2>
        <p>Vi behandlar personuppgifter baserat på följande rättsliga grunder enligt GDPR:</p>

        <h3>Patientuppgifter (känsliga personuppgifter)</h3>
        <ul>
            <li><strong>Artikel 6.1.a - Samtycke:</strong> Patienten ger sitt samtycke till remissen hos optikern</li>
            <li><strong>Artikel 9.2.a - Uttryckligt samtycke:</strong> För behandling av hälsouppgifter</li>
        </ul>

        <h3>Hur samtycke inhämtas</h3>
        <p>Patienten lämnar sitt samtycke till optikern i samband med undersökningen, innan remissen skapas. Optikern ansvarar för att samtycke har inhämtats innan patientens uppgifter registreras i tjänsten.</p>

        <h3>Användaruppgifter</h3>
        <ul>
            <li><strong>Artikel 6.1.b - Avtal:</strong> Nödvändigt för att tillhandahålla tjänsten</li>
        </ul>

        <h3>Säkerhetsloggar</h3>
        <ul>
            <li><strong>Artikel 6.1.f - Berättigat intresse:</strong> Säkerställa systemsäkerhet och spårbarhet</li>
        </ul>

        <h2>5. Delning av uppgifter</h2>
        <p>Vi delar personuppgifter med följande mottagare:</p>

        <table>
            <thead>
                <tr>
                    <th>Mottagare</th>
                    <th>Syfte</th>
                    <th>Plats</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>COS/OpenServices (Cambio)</td>
                    <td>Överföring av remisser till vårdsystemet via FHIR-standard</td>
                    <td>Sverige</td>
                </tr>
                <tr>
                    <td>Identitetsleverantör (Keycloak)</td>
                    <td>Säker inloggning och autentisering</td>
                    <td>EU/EES</td>
                </tr>
            </tbody>
        </table>

        <p><strong>Inga överföringar utanför EU/EES:</strong> Alla personuppgifter behandlas inom EU/EES.</p>

        <h2>6. Lagringstid</h2>
        <table>
            <thead>
                <tr>
                    <th>Uppgiftstyp</th>
                    <th>Lagringstid</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Remiss-ID (hashat)</td>
                    <td>Så länge remissen är aktiv i vårdsystemet</td>
                </tr>
                <tr>
                    <td>Statusuppgifter</td>
                    <td>5 år efter remissen avslutats</td>
                </tr>
                <tr>
                    <td>Användaruppgifter</td>
                    <td>Till dess kontot avslutas, plus 5 år</td>
                </tr>
                <tr>
                    <td>Säkerhetsloggar</td>
                    <td>5 år</td>
                </tr>
            </tbody>
        </table>

        <h2>7. Dina rättigheter</h2>
        <p>Enligt GDPR har du följande rättigheter:</p>
        <ul>
            <li><strong>Rätt till tillgång (Art. 15)</strong> - Du kan begära information om vilka uppgifter vi har om dig</li>
            <li><strong>Rätt till rättelse (Art. 16)</strong> - Du kan begära att felaktiga uppgifter rättas</li>
            <li><strong>Rätt till radering (Art. 17)</strong> - Du kan begära att uppgifter raderas, med undantag för lagkrav</li>
            <li><strong>Rätt till begränsning (Art. 18)</strong> - Du kan begära begränsad behandling under vissa omständigheter</li>
            <li><strong>Rätt till dataportabilitet (Art. 20)</strong> - Du kan begära att få ut dina uppgifter i maskinläsbart format</li>
            <li><strong>Rätt att invända (Art. 21)</strong> - Du kan invända mot behandling baserad på berättigat intresse</li>
            <li><strong>Rätt att återkalla samtycke</strong> - Du kan när som helst återkalla ett lämnat samtycke</li>
        </ul>

        <p>För att utöva dina rättigheter, kontakta oss på: christian.hilmersson@achilmersson.se</p>

        <h2>8. Säkerhet</h2>
        <p>Vi skyddar dina personuppgifter genom följande åtgärder:</p>
        <ul>
            <li><strong>Krypterad kommunikation</strong> - All datatrafik sker över HTTPS</li>
            <li><strong>Krypterade känsliga uppgifter</strong> - Systemhemligheter lagras krypterat (AES-256)</li>
            <li><strong>Hashade identifierare</strong> - Personnummer lagras inte i tjänsten. Endast ett icke-reversibelt hashvärde av patientens identifierare i vårdsystemet sparas</li>
            <li><strong>Rollbaserad åtkomstkontroll</strong> - Användare ser endast data de har behörighet till</li>
            <li><strong>Säkerhetsloggning</strong> - Inloggningar, remisshändelser och ändringar av användare och organisationer loggas för spårbarhet</li>
        </ul>

        <h2>9. Cookies</h2>
        <p>Ögonremiss använder följande cookies:</p>
        <table>
            <thead>
                <tr>
                    <th>Cookie</th>
                    <th>Syfte</th>
                    <th>Varaktighet</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Sessionscookie</td>
                    <td>Hålla dig inloggad under sessionen</td>
                    <td>Till webbläsaren stängs</td>
                </tr>
            </tbody>
        </table>
        <p>Vår inloggningstjänst använder dessutom nödvändiga cookies för säker autentisering. Vi använder <strong>inga</strong> tredjepartscookies för spårning eller marknadsföring.</p>

        <h2>10. Ändringar i policyn</h2>
        <p>Vi kan komma att uppdatera denna integritetspolicy. Vid väsentliga ändringar informerar vi dig via tjänsten eller e-post. Senaste version finns alltid tillgänglig på denna sida.</p>

        <h2>11. Kontakt och klagomål</h2>
        <p><strong>Kontakta oss:</strong></p>
        <p>
            E-post: christian.hilmersson@achilmersson.se
        </p>

        <p><strong>Tillsynsmyndighet:</strong></p>
        <p>Om du anser att vi behandlar dina personuppgifter i strid med GDPR har du rätt att lämna klagomål till:</p>
        <p>
            <strong>Integritetsskyddsmyndigheten (IMY)</strong><br/>
            Box 8114<br/>
            104 20 Stockholm<br/>
            <a href="https://www.imy.se" target="_blank" rel="noopener noreferrer">www.imy.se</a>
        </p>
    </div>
)

export default PrivacyPolicy